import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import styled from 'styled-components'
import moment, { Moment } from 'moment'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import { useSessionContext } from 'hooks/SessionContext'

import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'

import Api from 'services/api'

import { useConfirmDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type Props = {
  isPinned: boolean
  displayUnpinned: boolean
  customerName: string
  customerId: string
  pinnedAt?: Date | Moment | null
  className?: string
  refreshPageCallback?: () => void
}

const Pin = ({ isPinned, displayUnpinned, customerName, customerId, pinnedAt = moment(), className = '', refreshPageCallback }: Props) => {
  const { session: { dealerId } } = useSessionContext()
  const location = useLocation()

  const [isDisplayedAsPinned, setIsDisplayedAsPinned] = useState(isPinned)
  const [displayedPinnedAt, setDisplayedPinnedAt] = useState(pinnedAt)
  const { confirm } = useConfirmDialog()

  const tooltipTitle = isDisplayedAsPinned
    ? `Pinned on ${moment(displayedPinnedAt).format('M/DD/YYYY')} at ${moment(displayedPinnedAt).format('h:mm A')}`
    : 'Pin this customer to the top of the customer list'

  const onClick = (event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.stopPropagation();

    (async () => {
      const isConfirmed = isDisplayedAsPinned
        ? await confirm({
          title: CONFIRMATION_DIALOG_MESSAGES.UNPIN.TITLE(customerName),
          description: CONFIRMATION_DIALOG_MESSAGES.UNPIN.DESCRIPTION(customerName),
          confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.CONFIRM
        })
        : true

      if ((isDisplayedAsPinned && isConfirmed) || !isDisplayedAsPinned) {
        Api.DiagnosticsCustomers.update(dealerId, customerId, {
          pinned: !isDisplayedAsPinned
        }).then((res) => {
          if (!isDisplayedAsPinned) {
            const eventName = location.pathname.includes('/dashboard')
              ? 'pin_customer_from_dashboard'
              : 'pin_customer_from_customers'
            Apptentive.sendEvent(eventName)
          } else {
            refreshPageCallback?.()
          }

          const gaCategory = location.pathname.includes('/dashboard') ? 'Dashboard Page' : 'Customer List Page'
          GA4.sendEvent({
            category: gaCategory,
            action: isDisplayedAsPinned ? 'Unpin Customer' : 'Pin Customer',
            label: customerName
          })

          setIsDisplayedAsPinned(!isDisplayedAsPinned)
          setDisplayedPinnedAt(res.pinnedAt)
        })
      }
    })()
  }

  return (
    <Tooltip title={tooltipTitle}>
      <S.Pin
        {...{ onClick, displayUnpinned, className }}
        isPinned={isDisplayedAsPinned}
        aria-label={`${isDisplayedAsPinned ? 'unpin' : 'pin'} ${customerName}`}
        data-testid="pin"
      >

        <S.PinImage src={'/img/pin.svg'} alt="" aria-hidden isPinned={isDisplayedAsPinned} displayUnpinned={displayUnpinned} />
      </S.Pin>
    </Tooltip>
  )
}

export default Pin

const S = {
  Pin: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['displayUnpinned', 'isPinned'].includes(prop)
  })<{ isPinned: boolean, displayUnpinned: boolean }>`
    &:focus {
      img {
        ${({ isPinned }) => isPinned
    ? 'opacity: 1; transform: none;'
    : 'opacity: 0.45; transform: rotate(-25deg);'
}
      }
    }
  `,
  PinImage: styled.img.withConfig({
    shouldForwardProp: (prop) => !['displayUnpinned', 'isPinned'].includes(prop)
  })<{ isPinned: boolean, displayUnpinned: boolean }>`
    font-size: 15px;
    cursor: pointer;
    transition: all .2s ease;
    display: block;
    margin-top: -3px;

    ${({ isPinned, displayUnpinned }) => {
    if (isPinned) {
      return `
          opacity: 1;
          transform: none;
        `
    } else if (displayUnpinned) {
      return `
          opacity: 0.45;
          transform: rotate(-25deg);
        `
    } else {
      return 'opacity: 0;'
    }
  }}
  `
}
